import React from "react";
import { Badge, Progress, Space, TableProps } from "antd";
import { classNames } from "src/common/classNames";
import { t } from "i18next";
import { ColumnType } from "antd/es/table";
import { viewDate } from "src/common/formats";
import { ZUid } from "src/types/zUid";
import {
  SchedCodeType,
  SchedPriorityType,
  SchedRowType,
  SchedStateType,
  ZSchedCodeNumber,
  ZSchedCodeString,
  ZSchedPriority,
  ZSchedState,
  ZSchedulingRow,
  ZSchedulingSettings,
} from "../../SchedulingTypes";
import styles from "./SchedulingTable.module.less";
import brokenLinkIcon from "./brokenLinkIcon.svg";

const buildDefaultProps = (
  key: keyof ZSchedulingRow,
  title: string,
): ColumnType<ZSchedulingRow> => ({
  key,
  dataIndex: key,
  title,
  className: classNames([styles.cellEllipsis, styles.cellSize]),
});

const renderCodeCell = (
  val: ZSchedCodeNumber | ZSchedCodeString | null,
  customRender?: (val: number | string) => React.ReactNode,
) => {
  if (!val) return null;
  const { value, code } = val;
  return (
    <div
      className={classNames([
        [code === SchedCodeType.changed, styles.cellChanged],
      ])}
    >
      {customRender?.(value) ?? value}
    </div>
  );
};

const buildDefCodeProps = (
  key: keyof ZSchedulingRow,
  title: string,
): ColumnType<ZSchedulingRow> => ({
  ...buildDefaultProps(key, title),
  render: (val: ZSchedCodeNumber | ZSchedCodeString | null) =>
    renderCodeCell(val),
});

const buildDefaultDateProps = (
  key: keyof ZSchedulingRow,
  title: string,
): ColumnType<ZSchedulingRow> => ({
  ...buildDefaultProps(key, title),
  render: (value: string | null) => (value ? viewDate(value) : null),
});

const buildDefCodeDateProps = (
  key: keyof ZSchedulingRow,
  title: string,
): ColumnType<ZSchedulingRow> => ({
  ...buildDefaultProps(key, title),
  render: (val: ZSchedCodeString | null) =>
    renderCodeCell(val, () => (val?.value ? viewDate(val?.value) : null)),
});

const buildExtraColumn = (
  column: ColumnType<ZSchedulingRow>,
  settingsValue: ZUid | null | undefined,
): ColumnType<ZSchedulingRow>[] => (settingsValue ? [column] : []);

export const buildColumns = (
  settings: ZSchedulingSettings,
): TableProps["columns"] => [
  {
    ...buildDefaultProps("name", t("Task group / task")),
    className: classNames([
      styles.cellEllipsis,
      styles.cellSize,
      styles.cellName,
    ]),
    fixed: "left",
    render: (_, row: ZSchedulingRow) => {
      const { name } = row;
      if (!name) return null;
      const { value, code } = name;
      return (
        <Space
          className={classNames([
            [
              row.type === SchedRowType.plan || row.type === SchedRowType.group,
              styles.bold,
            ],
          ])}
        >
          {code === SchedCodeType.brokenLink && (
            <img src={brokenLinkIcon} alt="" />
          )}
          <span>
            {value}
            {row.type === SchedRowType.group &&
              row.children?.length &&
              `: ${row.children?.length}`}
          </span>
        </Space>
      );
    },
  },
  {
    ...buildDefaultProps("state", t("State")),
    className: styles.cellSize,
    render: (state: ZSchedState) => {
      if (!state) return null;
      const { progress, name, label, code } = state;
      return Number.isInteger(progress) ? (
        <div
          className={classNames([
            [code === SchedCodeType.changed, styles.cellChanged],
          ])}
        >
          <Progress percent={progress} strokeColor="#22BA7A" />
        </div>
      ) : (
        <div className={classNames([styles.badge, styles.cellEllipsis])}>
          <Badge text={label} color={name ? stateColorMap[name] : undefined} />
        </div>
      );
    },
  },
  {
    ...buildDefaultProps("priority", t("Priority")),
    className: styles.cellSize,
    render: (priority: ZSchedPriority) => {
      if (!priority) return null;
      const { name, label } = priority;
      return (
        <div className={classNames([styles.badge, styles.cellEllipsis])}>
          <Badge text={label} color={priorityColorMap[name]} />
        </div>
      );
    },
  },
  buildDefaultProps("division", t("Division")),
  buildDefaultProps("performer", t("Performer")),
  buildDefaultProps("performerLoad", t("Performer load per task per day")),
  buildDefCodeProps("plannedDuration", t("Planned duration")),
  buildDefaultProps("actualDuration", t("Actual duration")),
  ...buildExtraColumn(
    buildDefaultProps("supplier", t("Supplier")),
    settings.taskSettings?.supplierNameAttributeId,
  ),
  buildDefCodeDateProps("plannedStartDate", t("Planned start date")),
  buildDefCodeDateProps("plannedEndDate", t("Planned end date")),
  buildDefaultDateProps("actualStartDate", t("Actual start date")),
  buildDefaultDateProps("actualEndDate", t("Actual end date")),
  buildDefaultProps("deltaDays", t("Delay / override")),
];

const stateColorMap: Record<SchedStateType, string> = {
  [SchedStateType.created]: "#BAE7FF",
  [SchedStateType.progress]: "#1890FF",
  [SchedStateType.completed]: "#22BA7A",
};

const priorityColorMap: Record<SchedPriorityType, string> = {
  [SchedPriorityType.low]: "#FFC8A3",
  [SchedPriorityType.medium]: "#FC8A51",
  [SchedPriorityType.high]: "#BD2C00",
};
